import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertTriangle, ShieldAlert } from 'lucide-react';
import { Button } from './ui/button';

interface RequiresAuthWarningProps {
  lastError?: string;
  lastErrorAt?: string;
  authFailures?: number;
  onConfigure?: () => void;
}

export const RequiresAuthWarning = ({
  lastError,
  lastErrorAt,
  authFailures = 0,
  onConfigure
}: RequiresAuthWarningProps) => {
  return (
    <Alert variant="destructive" className="border-orange-500 bg-orange-50 dark:bg-orange-950/20">
      <ShieldAlert className="h-5 w-5 text-orange-600" />
      <AlertTitle className="text-orange-900 dark:text-orange-100 font-semibold">
        Authentication Required
      </AlertTitle>
      <AlertDescription className="text-orange-800 dark:text-orange-200 space-y-2">
        <p>
          The BillPortal API requires authentication. The integration received a 401/403 response
          and cannot proceed without valid credentials.
        </p>
        
        {lastError && (
          <p className="text-sm mt-2 font-mono bg-orange-100 dark:bg-orange-900/30 p-2 rounded">
            {lastError}
          </p>
        )}
        
        {authFailures > 0 && (
          <p className="text-sm flex items-center gap-2 mt-2">
            <AlertTriangle className="h-4 w-4" />
            <span>{authFailures} authentication failure{authFailures > 1 ? 's' : ''} detected</span>
          </p>
        )}
        
        {lastErrorAt && (
          <p className="text-xs text-orange-700 dark:text-orange-300 mt-1">
            Last failure: {new Date(lastErrorAt).toLocaleString()}
          </p>
        )}

        <div className="mt-4 space-y-2">
          <p className="font-medium">What to do:</p>
          <ol className="list-decimal list-inside space-y-1 text-sm">
            <li>Obtain a valid Bearer Token from BillPortal</li>
            <li>Click "Configure Authentication" below</li>
            <li>Enter your Bearer Token in the settings</li>
            <li>Click "Test Connection" to verify</li>
          </ol>
        </div>

        {onConfigure && (
          <Button 
            onClick={onConfigure}
            variant="outline"
            className="mt-4 border-orange-600 text-orange-700 hover:bg-orange-100 dark:hover:bg-orange-900/40"
          >
            Configure Authentication
          </Button>
        )}

        <p className="text-xs text-orange-600 dark:text-orange-400 mt-4">
          Note: While authentication is pending, bills will be processed using OpenAI fallback (if enabled).
        </p>
      </AlertDescription>
    </Alert>
  );
};
